import { CHECKLIST_POINTS, sitesForSub, allSites } from "@/components/briefing/checklistData";
import { fuelPlans, wbEffective, wbChartData } from "@/lib/navcalc";
import { fmt, minToHm } from "@/lib/units";
import { selectedVacCodes } from "@/lib/vac";

// Spécification JSON du dossier PDF : le backend la met en page et insère les pièces jointes.
const navTable = (title, sec) => ({
  type: "table", title,
  header: ["Point", "Alt", "Rv", "Décl", "Vent", "Dérive", "Cv", "Cm", "Cc", "Dist", "Vsol", "Temps", "Cumul", "Carbu"],
  rows: [
    ...sec.rows.map((r, i) => [
      r.name || `Pt ${i + 1}`, fmt(r.altitude), `${fmt(r.trackTrue)}°`, fmt(r.variation, 1), `${fmt(r.windDir)}°/${fmt(r.windSpeed)}`,
      `${r.wca > 0 ? "+" : ""}${r.wca}°`, `${fmt(r.headingTrue)}°`, `${fmt(r.headingMag)}°`, `${fmt(r.headingCompass)}°`,
      fmt(r.distance), fmt(r.gs), `${fmt(r.timeMin)}'`, minToHm(r.cumTime), `${fmt(r.fuel, 1)} L`,
    ]),
    ["TOTAUX", "", "", "", "", "", "", "", "", `${fmt(sec.totalDistance)} NM`, "", minToHm(sec.totalTime), "", `${fmt(sec.totalFuel, 1)} L`],
  ],
  notes: sec.rows.map((r, i) => (r.frequencies || []).length
    ? `${r.name || `Pt ${i + 1}`} — Fréq. : ${r.frequencies.map((f) => `${f.type} ${f.name || ""} ${f.freq || ""}`.replace(/\s+/g, " ").trim()).join(" · ")}`
    : null).filter(Boolean),
});

function point5(briefing, nav) {
  const sections = nav.ret ? [["Aller", nav.out], ["Retour", nav.ret]] : [["Log de navigation", nav.out]];
  return [
    { type: "kv", rows: [["Aérodrome de départ", briefing.departure || "—"], ["Arrivée", briefing.arrival || "—"]] },
    ...sections.map(([t, s]) => navTable(t, s)),
    ...(nav.ret ? [{ type: "text", text: `Total A/R : ${fmt(nav.totalDistance)} NM · ${minToHm(nav.totalTime)} · ${fmt(nav.totalFuel, 1)} L` }] : []),
    { type: "subheading", text: "Carte de navigation" },
    briefing.navMapComment ? { type: "text", text: briefing.navMapComment } : { type: "empty" },
    { type: "attachments", point_id: "navmap" },
  ];
}

function point6(briefing, nav, aircraft) {
  const burn = Number(aircraft?.fuelBurn) || 0;
  const plans = fuelPlans(briefing, nav, aircraft);
  const t = (l) => minToHm(burn > 0 ? (l / burn) * 60 : 0);
  const params = {
    type: "table", title: "Paramètres carburant",
    header: ["Désignation", "Nombre", "Temps standard", "Temps (min)", "Litrage (L)"],
    rows: plans[0].plan.rows.map((r) => [
      r.label, r.kind === "count" ? String(r.count) : "—",
      r.kind === "count" ? `${fmt(r.std)} min` : r.kind === "reserve" ? `${r.night ? "Nuit" : "Jour"} · ${r.std} min` : "—",
      r.kind === "free" && (r.entry.v === "" || r.entry.v === undefined) ? "—" : fmt(r.min),
      r.kind === "free" && (r.entry.v === "" || r.entry.v === undefined) ? "—" : fmt(r.L, 1),
    ]),
  };
  const bilans = plans.map(({ title, plan }) => ({
    type: "table", title: `Bilan carburant${title ? ` — ${title}` : ""}`, header: ["Poste", "Litres", "Temps"],
    rows: [
      ["Forfait roulage", plan.taxi], ["Voyage (log de nav)", plan.trip], ["Marge aléas", plan.contingency],
      ["Forfait procédure", plan.procedure], ["Alternative déroutement", plan.alternate], ["Réserve réglementaire VFR", plan.reserve],
      ["Carburant non utilisable", plan.unusable], ["Minimum réglementaire", plan.minRequired], ["Réserve exploitant CDB", plan.cdb],
      ["Total requis", plan.totalRequired], ["Carburant embarqué", plan.onboard], ["Marge", plan.margin],
    ].map(([l, v]) => [l, `${fmt(v, 1)} L`, `${v < 0 ? "−" : ""}${t(Math.abs(v))}`]),
    highlight: plan.margin >= 0 ? "ok" : "ko",
  }));
  return [params, ...bilans, { type: "text", text: `Capacité ${aircraft?.fuelCapacity || 0} L · Conso ${burn} L/h · Autonomie embarquée ${minToHm(plans[0].plan.endurance)}`, muted: true }];
}

function point7(briefing, nav, aircraft) {
  const wb = wbEffective(briefing, aircraft, nav);
  const d = wbChartData(wb, aircraft, briefing, nav);
  const totalW = wb.items.reduce((s, i) => s + (Number(i.weight) || 0), 0);
  const totalM = wb.items.reduce((s, i) => s + (Number(i.moment) || 0), 0);
  const c = (p) => ({ label: p.label, cg: p.cg, weight: p.weight });
  return [
    {
      type: "table", title: "Détail du calcul (carburant à 0,725 kg/L)", header: ["Élément", "Masse (kg)", "Bras (m)", "Moment (kg·m)"],
      rows: [...wb.items.map((i) => [i.name, fmt(i.weight, 1), fmt(i.arm, 3), fmt(i.moment, 1)]),
        ["Total départ", fmt(totalW, 1), totalW > 0 ? fmt(totalM / totalW, 3) : "—", fmt(totalM, 1)]],
    },
    {
      type: "kv", rows: [
        ["Décollage", `${fmt(wb.takeoff.weight, 2)} kg · CG ${fmt(wb.takeoff.cg, 2)} m · ${wb.takeoff.ok ? "dans l'enveloppe" : "HORS LIMITES"}`],
        ...d.landings.map((l) => [l.label, `${fmt(l.weight, 2)} kg · CG ${fmt(l.cg, 2)} m`]),
        ["Carburant mini", `${fmt(d.minFuel.weight, 2)} kg · CG ${fmt(d.minFuel.cg, 2)} m`],
        ["Marge masse décollage", wb.mtow ? `${fmt(wb.mtow - wb.takeoff.weight)} kg (MTOW ${fmt(wb.mtow)} kg)` : "—"],
      ],
    },
    { type: "wbchart", envelope: d.envelope.map((p) => [p.cg, p.weight]), takeoff: c(d.takeoff), minFuel: c(d.minFuel), landings: d.landings.map(c) },
  ];
}

function point8(briefing) {
  const items = briefing.limitations || [];
  if (!items.length) return [{ type: "empty", text: "Aucun terrain renseigné" }];
  return [{
    type: "table", title: "Limitations terrains", header: ["Terrain", "TORA (m)", "Déco requis (m)", "Déco", "LDA (m)", "Atterr. requis (m)", "Atterr."],
    rows: items.map((it) => [it.airport || "—", fmt(it.tora), fmt(it.requiredTakeoff), Number(it.tora) >= Number(it.requiredTakeoff) ? "OK" : "INSUFFISANT",
      fmt(it.lda), fmt(it.requiredLanding), Number(it.lda) >= Number(it.requiredLanding) ? "OK" : "INSUFFISANT"]),
  }];
}

const links = (briefingX, subId, override) => {
  const pick = override ? allSites(briefingX).filter((s) => override.includes(s.id)) : sitesForSub(briefingX, subId);
  return pick.length ? [{ type: "links", items: pick.map((s) => ({ name: s.name, url: s.url })) }] : [];
};

export function buildPdfSpec({ briefing, briefingX, aircraft, nav }) {
  const previews = { 5: () => point5(briefing, nav), 6: () => point6(briefing, nav, aircraft), 7: () => point7(briefing, nav, aircraft), 8: () => point8(briefing) };
  const points = CHECKLIST_POINTS.map((p) => {
    const st = briefing.checklist?.[p.id] || {};
    const subs = [...(p.fields || []).map((f) => ({ key: f.key, label: f.label })), ...(st.customSubs || []).map((c) => ({ key: c.key, label: c.label }))];
    const blocks = [
      { type: "text", text: p.purpose, muted: true },
      { type: "bullets", items: p.checks },
      ...(previews[p.id] ? previews[p.id]() : []),
      ...(p.entity === "pilot" && briefing.pilotId ? [{ type: "entity_docs", parent_type: "pilots", parent_id: briefing.pilotId, title: "Documents du pilote (fiche)" }] : []),
      ...(p.entity === "aircraft" && briefing.aircraftId ? [{ type: "entity_docs", parent_type: "aircraft", parent_id: briefing.aircraftId, title: "Documents avion (fiche)" }] : []),
      ...subs.flatMap((s) => [
        { type: "subheading", text: s.label },
        st[s.key] ? { type: "text", text: String(st[s.key]) } : { type: "empty" },
        ...links(briefingX, `${p.id}::${s.key}`, st.subSites?.[s.key]),
        { type: "attachments", point_id: `${p.id}::${s.key}` },
      ]),
      { type: "attachments", point_id: String(p.id), title: "Autres documents du point" },
    ];
    return { heading: `${p.id}. ${p.title}`, status: st.done ? "Vérifié" : "À faire", blocks };
  });

  const codes = selectedVacCodes(briefing);
  const vac = {
    heading: "Cartes VAC des terrains", newPage: true,
    blocks: codes.length ? codes.flatMap((c) => [{ type: "subheading", text: `Carte VAC ${c}` }, { type: "attachments", point_id: `vac::${c}`, emptyText: true, layout: "2up" }])
      : [{ type: "empty", text: "Aucun terrain renseigné" }],
  };

  return {
    title: `Dossier de navigation VFR — ${briefing.name || ""}`,
    subtitle: `${briefing.departure || "?"} → ${briefing.arrival || "?"}${nav.ret ? ` → ${briefing.departure || "?"}` : ""} · Avion : ${aircraft?.registration || "—"} (${aircraft?.model || ""}) · Pilote : ${briefing.pilotName || "—"}`,
    summary: `${fmt(nav.totalDistance)} NM · ${minToHm(nav.totalTime)} · ${fmt(nav.totalFuel, 1)} L · TAS ${aircraft?.tasCruise || 0} kt`,
    sections: [...points, vac],
  };
}
